import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  LayoutDashboard, Ticket, Users, Wallet, TrendingUp, LogOut, Settings, Share2, IndianRupee, MessageSquare,
} from "lucide-react";
import PMRLogo from "../components/PMRLogo";
import { ADMIN_MOCK } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const statusStyles = {
  Confirmed: "bg-green-100 text-green-700 border-green-200",
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
};

const AgentDashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("pmr_agent");
    if (stored) setAgent(JSON.parse(stored));
  }, []);

  if (agent === null && !localStorage.getItem("pmr_agent")) {
    return <Navigate to="/agent/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem("pmr_agent");
    toast({ title: "Signed out", description: "See you soon!" });
    navigate("/agent/login");
  };

  const shareReferral = () => {
    navigator.clipboard.writeText(`https://pmrtravels.com?ref=${agent?.agentId || "PMRAG"}`);
    toast({ title: "Referral link copied!", description: "Share with clients to earn commission." });
  };

  const d = ADMIN_MOCK.agentDashboard;

  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 w-64 bg-slate-950 text-white p-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <PMRLogo size={28} color="#0f766e" accent="#f97316" />
          </div>
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>PMR Agent</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Portal</div>
          </div>
        </Link>

        <nav className="mt-8 space-y-1 flex-1">
          <a href="#dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white font-medium text-sm">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </a>
          <a href="#bookings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <Ticket className="w-4 h-4" /> My Bookings
          </a>
          <a href="#clients" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <Users className="w-4 h-4" /> Clients
          </a>
          <a href="#wallet" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <Wallet className="w-4 h-4" /> Commission Wallet
          </a>
          <a href="#settings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <Settings className="w-4 h-4" /> Settings
          </a>
        </nav>

        <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-red-300 text-sm">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <main className="lg:ml-64">
        <div className="sticky top-0 bg-white border-b border-slate-200 z-10">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-slate-500">Welcome back,</div>
              <div className="font-bold text-slate-900">{agent?.name || "Agent"}</div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={shareReferral} className="bg-orange-500 hover:bg-orange-600 gap-2 h-10">
                <Share2 className="w-4 h-4" /> Share Referral
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white font-bold flex items-center justify-center text-sm">
                  {agent?.name?.charAt(0) || "A"}
                </div>
                <div className="hidden md:block">
                  <div className="text-sm font-semibold text-slate-900">{agent?.name}</div>
                  <div className="text-xs text-slate-500">{agent?.role} · {agent?.agentId}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-8">
          {/* Commission wallet hero */}
          <div className="relative rounded-3xl overflow-hidden p-8 bg-gradient-to-br from-teal-700 via-teal-600 to-teal-500 text-white shadow-xl">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/80">
                  <Wallet className="w-3.5 h-3.5" /> Commission Wallet
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <IndianRupee className="w-7 h-7" />
                  <span className="text-5xl font-bold tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {d.walletBalance.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="mt-1 text-white/85 text-sm">Ready to withdraw</div>
              </div>
              <div className="flex gap-2">
                <Button className="bg-white text-teal-700 hover:bg-slate-100">Withdraw</Button>
                <Button variant="outline" className="border-white/50 text-white bg-white/10 hover:bg-white hover:text-teal-700">Transaction History</Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Bookings Made", value: d.bookingsMade, icon: Ticket, tint: "bg-teal-50 text-teal-700" },
              { label: "Total Commission", value: `₹${d.totalCommission.toLocaleString("en-IN")}`, icon: IndianRupee, tint: "bg-orange-50 text-orange-600" },
              { label: "This Month", value: `₹${d.thisMonthCommission.toLocaleString("en-IN")}`, icon: TrendingUp, tint: "bg-emerald-50 text-emerald-700" },
              { label: "Clients Served", value: d.clientsServed, icon: Users, tint: "bg-violet-50 text-violet-700" },
            ].map((s) => (
              <div key={s.label} className="bg-white p-5 rounded-2xl border border-slate-200 hover:shadow-lg transition">
                <div className={`w-10 h-10 rounded-lg ${s.tint} flex items-center justify-center`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="mt-4 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>{s.value}</div>
                <div className="mt-0.5 text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Recent bookings */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900">Recent Bookings by You</h3>
                <p className="text-xs text-slate-500">Conversion rate: <span className="text-teal-700 font-semibold">{d.conversionRate}</span></p>
              </div>
              <Button asChild variant="outline" size="sm"><a href="#all">View all</a></Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    <th className="text-left px-6 py-3 font-semibold">Booking ID</th>
                    <th className="text-left px-6 py-3 font-semibold">Client</th>
                    <th className="text-left px-6 py-3 font-semibold">Type</th>
                    <th className="text-right px-6 py-3 font-semibold">Amount</th>
                    <th className="text-right px-6 py-3 font-semibold">Commission</th>
                    <th className="text-left px-6 py-3 font-semibold">Status</th>
                    <th className="text-left px-6 py-3 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {d.recentBookings.map((b) => (
                    <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 font-mono font-semibold text-slate-900">{b.id}</td>
                      <td className="px-6 py-4 text-slate-800">{b.client}</td>
                      <td className="px-6 py-4"><Badge variant="outline" className="text-xs">{b.type}</Badge></td>
                      <td className="px-6 py-4 text-right font-semibold text-slate-900">₹{b.amount.toLocaleString("en-IN")}</td>
                      <td className="px-6 py-4 text-right font-bold text-teal-700">+₹{b.commission.toLocaleString("en-IN")}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusStyles[b.status]}`}>{b.status}</span></td>
                      <td className="px-6 py-4 text-slate-500">{b.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pt-2 pb-8 text-center text-xs text-orange-600 font-semibold">
            DEMO MODE — Agent portal is MOCKED (no real backend or commissions).
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentDashboardPage;
